/**
 * Triage — the work queue. Master/detail with keyboard navigation.
 */
import { useMemo, useState } from "react";
import { ConnectorGate } from "@/components/connectors/ConnectorGate";
import { SurfaceShell, StatTile } from "@/components/surface/SurfaceShell";
import { WorkQueue } from "@/components/surface/WorkQueue";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useEdrAlerts } from "@/hooks/security/useEdrData";
import { useCaseAction } from "@/hooks/security/useEdrMutations";
import { OwnerPill } from "@/components/owner-pill";
import { CheckCircle2, XCircle, ArrowUpRight, ShieldAlert } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RespondPanel } from "@/components/security/rtr/RespondPanel";
import { formatDistanceToNow } from "date-fns";
import type { EdrAlert, AlertSeverity } from "@/types/edr";

const SEV_COLOR: Record<AlertSeverity, string> = {
  critical: "bg-destructive",
  high: "bg-destructive/70",
  medium: "bg-warning",
  low: "bg-info",
  info: "bg-muted-foreground/50",
};
const SEV_RANK: Record<AlertSeverity, number> = {
  critical: 4, high: 3, medium: 2, low: 1, info: 0,
};

export default function TriagePage() {
  return (
    <ConnectorGate connectorId="edr">
      <TriageInner />
    </ConnectorGate>
  );
}

function TriageInner() {
  const alertsQ = useEdrAlerts({ limit: 500 });
  const { data: alerts = [], isLoading, isError, error, refetch } = alertsQ;
  const [query, setQuery] = useState("");
  const [sev, setSev] = useState<string>("all");
  const [status, setStatus] = useState<string>("open");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const filtered = useMemo(() => {
    return alerts
      .filter((a) => (status === "all" ? true : status === "open" ? a.status === "new" : a.status === status))
      .filter((a) => (sev === "all" ? true : a.severity === sev))
      .filter((a) =>
        query
          ? `${a.ruleName} ${a.deviceName} ${a.summary} ${a.mitre}`
              .toLowerCase()
              .includes(query.toLowerCase())
          : true,
      )
      .sort((a, b) =>
        SEV_RANK[b.severity] - SEV_RANK[a.severity] ||
        new Date(b.ts).getTime() - new Date(a.ts).getTime(),
      );
  }, [alerts, sev, status, query]);

  const open = alerts.filter((a) => a.status === "new");
  const critical = open.filter((a) => a.severity === "critical").length;
  const oldest = open[open.length - 1];
  const oldestAge = oldest ? formatDistanceToNow(new Date(oldest.ts), { addSuffix: false }) : "—";

  const selected = filtered.find((a) => a.id === selectedId) ?? filtered[0];

  return (
    <SurfaceShell
      overline="Security · Triage"
      title="What needs your attention"
      summary={`${open.length} open alert${open.length === 1 ? "" : "s"}. ${critical} critical. Oldest waiting ${oldestAge}.`}
      kpis={
        <>
          <StatTile label="Open" value={open.length} loading={isLoading || isError} tone={open.length > 0 ? "warning" : "success"} />
          <StatTile label="Critical" value={critical} loading={isLoading || isError} tone={critical > 0 ? "danger" : "success"} />
          <StatTile label="Oldest waiting" value={oldestAge} loading={isLoading || isError} hint="Time since detection" />
          <StatTile label="Assigned to you" value={0} loading={isLoading || isError} hint="Pick from the queue" />
        </>
      }
    >
      <WorkQueue
        loading={isLoading}
        isError={isError}
        error={error}
        onRetry={() => refetch()}
        emptyTitle={alerts.length === 0 ? "Queue is clear" : "No matches"}
        emptyLabel={alerts.length === 0 ? "No alerts in the queue right now." : "No alerts match this filter."}
        items={filtered.map((a) => ({ id: a.id, render: <AlertRow alert={a} /> }))}
        selectedId={selectedId}
        onSelect={setSelectedId}
        toolbar={
          <>
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search alerts…"
              className="h-8 text-xs"
            />
            <Select value={sev} onValueChange={setSev}>
              <SelectTrigger className="h-8 w-[110px] text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All severity</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="h-8 w-[110px] text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="escalated">Escalated</SelectItem>
                <SelectItem value="dismissed">Dismissed</SelectItem>
                <SelectItem value="all">All</SelectItem>
              </SelectContent>
            </Select>
          </>
        }
        renderDetail={(id) => {
          const a = filtered.find((x) => x.id === id) ?? selected;
          return a ? <AlertDetail alert={a} /> : <NoSelection />;
        }}
      />
      <div className="mt-3 text-[11px] text-muted-foreground">
        ↑ ↓ or J / K to move · Esc to clear selection
      </div>
    </SurfaceShell>
  );
}

function AlertRow({ alert }: { alert: EdrAlert }) {
  return (
    <div className="min-w-0">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className={`h-2 w-2 rounded-full flex-none ${SEV_COLOR[alert.severity]}`} />
          <span className="text-sm font-medium text-foreground truncate">{alert.ruleName}</span>
        </div>
        <span className="text-[10px] tabular-nums text-muted-foreground flex-none">
          {formatDistanceToNow(new Date(alert.ts), { addSuffix: false })}
        </span>
      </div>
      <div className="mt-1 text-[11px] text-muted-foreground truncate">
        {alert.deviceName} · {alert.mitre} · {alert.family}
      </div>
    </div>
  );
}

function NoSelection() {
  return (
    <div className="h-full grid place-items-center p-10 text-center">
      <div>
        <ShieldAlert className="h-8 w-8 mx-auto text-muted-foreground" />
        <p className="mt-3 text-sm text-muted-foreground">Select an alert to start triage.</p>
      </div>
    </div>
  );
}

function AlertDetail({ alert }: { alert: EdrAlert }) {
  const action = useCaseAction();
  return (
    <div className="flex flex-col h-full">
      <header className="px-6 py-4 border-b border-border">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <span className={`h-2 w-2 rounded-full ${SEV_COLOR[alert.severity]}`} />
              <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                {alert.severity} · {alert.status}
              </span>
            </div>
            <h2 className="mt-1 text-lg font-semibold text-foreground">{alert.ruleName}</h2>
            <p className="text-xs text-muted-foreground tabular-nums">
              {new Date(alert.ts).toLocaleString()}
            </p>
          </div>
          <div className="flex items-center gap-1.5">
            <Button size="sm" variant="outline"
              onClick={() => action.mutate({ caseId: alert.id, action: "assign" })}>
              Assign to me
            </Button>
            <Button size="sm" variant="outline"
              onClick={() => action.mutate({ caseId: alert.id, action: "escalate" })}>
              <ArrowUpRight className="h-3.5 w-3.5 mr-1" /> Escalate
            </Button>
            <Button size="sm" variant="ghost"
              onClick={() => action.mutate({ caseId: alert.id, action: "close" })}>
              <XCircle className="h-3.5 w-3.5 mr-1" /> Dismiss
            </Button>
          </div>
        </div>
      </header>

      <Tabs defaultValue="details" className="flex-1 flex flex-col min-h-0">
        <div className="px-6 pt-3">
          <TabsList>
            <TabsTrigger value="details">Details</TabsTrigger>
            <TabsTrigger value="respond">Respond</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="details" className="px-6 py-4 space-y-5 mt-0">
          <Field label="What happened">
            <p className="text-sm text-foreground leading-relaxed">{alert.summary || "—"}</p>
          </Field>

          <div className="grid grid-cols-2 gap-x-6 gap-y-3">
            <Field label="Device">
              <span className="text-sm font-mono text-foreground">{alert.deviceName}</span>
            </Field>
            <Field label="Owner">
              <OwnerPill
                consoleUser={(alert as unknown as { consoleUser?: string }).consoleUser}
                deviceId={alert.deviceId}
                size="sm"
              />
            </Field>
            <Field label="Process">
              <span className="text-sm font-mono text-foreground truncate block">{alert.process}</span>
            </Field>
            <Field label="MITRE">
              <span className="text-sm font-mono text-foreground">{alert.mitre}</span>
            </Field>
            <Field label="Family">
              <span className="text-sm text-foreground capitalize">{alert.family.replace(/_/g, " ")}</span>
            </Field>
          </div>

          <Field label="Suggested action">
            <div className="rounded border border-border bg-muted/40 px-3 py-2.5 text-sm text-foreground flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 mt-0.5 text-success flex-none" />
              <span>
                Verify the process on {alert.deviceName}, then isolate the sensor if the binary is
                unfamiliar. Once contained, escalate to investigation.
              </span>
            </div>
          </Field>
        </TabsContent>

        <TabsContent value="respond" className="px-6 py-4 mt-0">
          <RespondPanel
            alert={{
              id: alert.id,
              deviceId: alert.deviceId,
              deviceName: alert.deviceName,
              process: alert.process,
              pid: (alert as unknown as { pid?: number }).pid,
              ruleName: alert.ruleName,
            }}
          />
        </TabsContent>
      </Tabs>

    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
        {label}
      </div>
      <div className="mt-1">{children}</div>
    </div>
  );
}
